import cookies from 'js-cookie';
import i18next from '../i18n.ts';
import type { ICashDrawerConfig } from '../interfaces/Store';
import Printer from './qz-print';
import { createEscPosQzPrintData } from './ticketEscPos';

export const DEFAULT_CASH_DRAWER_COMMAND = new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]);

export type OpenCashDrawerOptions = {
  source?: 'manual' | 'payment' | 'test';
  printerId?: string;
  commandBytes?: Uint8Array | number[];
};

export type OpenCashDrawerResult = {
  success: boolean;
  errorMessage?: string;
};

export type CashDrawerAutoOpenPayment = {
  paymentMethod?: string | null;
  amountPaid?: number | null;
};

export type OrderCreateAutoOpenInput = {
  amountPaid?: number | null;
  paymentMethod?: string | null;
  prepaidAmount?: number | null;
  prepaidPaymentMethod?: string | null;
};

export type AutoOpenCashDrawerForPaymentsOptions = {
  cashDrawerConfig?: ICashDrawerConfig | null;
  payments: CashDrawerAutoOpenPayment[];
  source?: OpenCashDrawerOptions['source'];
};

export type AutoOpenCashDrawerForPaymentsResult = OpenCashDrawerResult & {
  attempted: boolean;
  matchedPaymentsCount: number;
};

const getDefaultCashDrawerError = (): string => i18next.t('Notifications:cashDrawerOpenFailedBody');
const SUPPORTED_AUTO_OPEN_PAYMENT_METHODS = new Set(['cash', 'card', 'transfer', 'mercadopago']);

const resolvePrinterName = (printerId?: string): string | undefined => {
  return printerId || cookies.get('printer') || localStorage.getItem('printer') || undefined;
};

const normalizeCommandBytes = (commandBytes?: Uint8Array | number[]): Uint8Array => {
  if (!commandBytes) return DEFAULT_CASH_DRAWER_COMMAND;
  return commandBytes instanceof Uint8Array ? commandBytes : new Uint8Array(commandBytes);
};

const normalizePaymentMethod = (paymentMethod?: string | null): string => {
  return `${paymentMethod || ''}`.trim().toLowerCase();
};

export const shouldAutoOpenCashDrawerForPayment = ({
  cashDrawerConfig,
  paymentMethod,
  amountPaid,
}: {
  cashDrawerConfig?: ICashDrawerConfig | null;
  paymentMethod?: string | null;
  amountPaid?: number | null;
}): boolean => {
  const openDrawerAfterPaymentCreated =
    cashDrawerConfig?.openDrawerAfterPaymentCreated || 'never';
  const normalizedPaymentMethod = normalizePaymentMethod(paymentMethod);
  const normalizedAmount = Number(amountPaid || 0);

  if (openDrawerAfterPaymentCreated === 'never') return false;
  if (!normalizedPaymentMethod || normalizedPaymentMethod === 'delivery') return false;
  if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) return false;

  if (openDrawerAfterPaymentCreated === 'cash') {
    return normalizedPaymentMethod === 'cash';
  }

  return SUPPORTED_AUTO_OPEN_PAYMENT_METHODS.has(normalizedPaymentMethod);
};

export const getOrderCreatePaymentsForAutoOpen = ({
  amountPaid,
  paymentMethod,
  prepaidAmount,
  prepaidPaymentMethod,
}: OrderCreateAutoOpenInput): CashDrawerAutoOpenPayment[] => {
  const normalizedPrepaidAmount = Number(prepaidAmount || 0);
  if (
    normalizedPrepaidAmount > 0 &&
    prepaidPaymentMethod &&
    normalizePaymentMethod(prepaidPaymentMethod) !== 'mercadopago'
  ) {
    return [{ paymentMethod: prepaidPaymentMethod, amountPaid: normalizedPrepaidAmount }];
  }

  const normalizedAmountPaid = Number(amountPaid || 0);
  if (
    normalizedAmountPaid > 0 &&
    paymentMethod &&
    normalizePaymentMethod(paymentMethod) !== 'mercadopago'
  ) {
    return [{ paymentMethod, amountPaid: normalizedAmountPaid }];
  }

  return [];
};

export const autoOpenCashDrawerForPayments = async ({
  cashDrawerConfig,
  payments,
  source = 'payment',
}: AutoOpenCashDrawerForPaymentsOptions): Promise<AutoOpenCashDrawerForPaymentsResult> => {
  const matchedPayments = payments.filter((payment) =>
    shouldAutoOpenCashDrawerForPayment({
      cashDrawerConfig,
      paymentMethod: payment.paymentMethod,
      amountPaid: payment.amountPaid,
    }),
  );

  if (!matchedPayments.length) {
    return {
      attempted: false,
      success: true,
      matchedPaymentsCount: 0,
    };
  }

  const result = await openCashDrawer({ source });

  return {
    attempted: true,
    matchedPaymentsCount: matchedPayments.length,
    ...result,
  };
};

export const openCashDrawer = async ({
  source,
  printerId,
  commandBytes,
}: OpenCashDrawerOptions = {}): Promise<OpenCashDrawerResult> => {
  void source;

  const printerName = resolvePrinterName(printerId);
  if (!printerName) {
    return {
      success: false,
      errorMessage: getDefaultCashDrawerError(),
    };
  }

  try {
    const printer = new Printer();
    const connectResponse = await printer.verifyQZConnection(printerName);

    if (!connectResponse.ok) {
      return {
        success: false,
        errorMessage: connectResponse.message || getDefaultCashDrawerError(),
      };
    }

    // Browsers cannot reliably open a physical drawer directly; this helper
    // depends on the existing QZ/printer bridge to send ESC/POS raw bytes.
    await printer.print(
      printerName,
      {
        jobName: 'Washday - Open Cash Drawer',
      },
      createEscPosQzPrintData(normalizeCommandBytes(commandBytes)),
    );

    return { success: true };
  } catch (error) {
    return {
      success: false,
      errorMessage: error instanceof Error ? error.message : getDefaultCashDrawerError(),
    };
  }
};
